import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Paper,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
  TableContainer,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  SvgIcon,
  Snackbar,
  Alert,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import axios from "../utils/axios-instance";
import { useQuery } from "@tanstack/react-query";
import { ServerRoutes } from "../routes";
import { SubscriptionTransaction, AppPagination } from "../types";
import { format } from "date-fns";
import { useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import { Dayjs } from "dayjs";
import {
  actionButtonStyles,
  actionButtonContainerStyle,
} from "../utils/buttonStyles";
import ReceiptIcon from "@mui/icons-material/Receipt";

// Telegram Icon Component
const TelegramIcon: React.FC = () => (
  <SvgIcon
    viewBox="0 0 256 256"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    preserveAspectRatio="xMidYMid"
  >
    <defs>
      <linearGradient id="a" x1="50%" x2="50%" y1="0%" y2="100%">
        <stop offset="0%" stopColor="#2AABEE" />
        <stop offset="100%" stopColor="#229ED9" />
      </linearGradient>
    </defs>
    <path
      fill="url(#a)"
      d="M128 0C94.06 0 61.48 13.494 37.5 37.49A128.038 128.038 0 0 0 0 128c0 33.934 13.5 66.514 37.5 90.51C61.48 242.506 94.06 256 128 256s66.52-13.494 90.5-37.49c24-23.996 37.5-56.576 37.5-90.51 0-33.934-13.5-66.514-37.5-90.51C194.52 13.494 161.94 0 128 0Z"
    />
    <path
      fill="#FFF"
      d="M57.94 126.648c37.32-16.256 62.2-26.974 74.64-32.152 35.56-14.786 42.94-17.354 47.76-17.441 1.06-.017 3.42.245 4.96 1.49 1.28 1.05 1.64 2.47 1.82 3.467.16.996.38 3.266.2 5.038-1.92 20.24-10.26 69.356-14.5 92.026-1.78 9.592-5.32 12.808-8.74 13.122-7.44.684-13.08-4.912-20.28-9.63-11.26-7.386-17.62-11.982-28.56-19.188-12.64-8.328-4.44-12.906 2.76-20.386 1.88-1.958 34.64-31.748 35.26-34.45.08-.338.16-1.598-.6-2.262-.74-.666-1.84-.438-2.64-.258-1.14.256-19.12 12.152-54 35.686-5.1 3.508-9.72 5.218-13.88 5.128-4.56-.098-13.36-2.584-19.9-4.708-8-2.606-14.38-3.984-13.82-8.41.28-2.304 3.46-4.662 9.52-7.072Z"
    />
  </SvgIcon>
);

// Fetch Subscriptions with pagination and filters
const fetchSubscriptions = async (
  page: number = 1,
  fromDate?: Dayjs | null,
  toDate?: Dayjs | null,
) => {
  const query = new URLSearchParams();
  query.append("page", page.toString());

  if (fromDate) query.append("from", fromDate.format("YYYY-MM-DD"));
  if (toDate) query.append("to", toDate.format("YYYY-MM-DD"));

  const response = await axios.get<AppPagination<SubscriptionTransaction>>(
    `${ServerRoutes.subscriptions}?${query.toString()}`,
    {
      headers: { "Content-Type": "application/json" },
    },
  );

  return response.data;
};

export function SubscriptionsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);

  // Filter states
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Dayjs | null>(null);
  const [tempToDate, setTempToDate] = useState<Dayjs | null>(null);

  // Subscription detail modal states
  const [selectedTransaction, setSelectedTransaction] =
    useState<SubscriptionTransaction | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isRenewalModalOpen, setIsRenewalModalOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [renewalDays, setRenewalDays] = useState<{ [key: string]: number }>({});
  const [bulkRenewalDays, setBulkRenewalDays] = useState<number>(30);

  // Telegram modal state
  const [isTelegramModalOpen, setIsTelegramModalOpen] = useState(false);
  const [telegramMessage, setTelegramMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [telegramSnackbarOpen, setTelegramSnackbarOpen] = useState(false);
  const [telegramSnackbarMessage, setTelegramSnackbarMessage] = useState("");
  const [telegramSnackbarSeverity, setTelegramSnackbarSeverity] = useState<
    "success" | "error"
  >("success");

  // Query for fetching subscriptions
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["subscriptions", page, fromDate?.format(), toDate?.format()],
    queryFn: () => fetchSubscriptions(page, fromDate, toDate),
  });

  // Format the expiry date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  // Check if subscription is expired
  const isExpired = (dateString: string) => {
    try {
      return new Date(dateString) < new Date();
    } catch (error) {
      return false;
    }
  };

  // Filter modal handlers
  const openFilterModal = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const applyFilters = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setPage(1);
    closeFilterModal();
    refetch();
  };

  const clearFilters = () => {
    setTempFromDate(null);
    setTempToDate(null);
  };

  // Check if there are active filters
  const hasActiveFilters = fromDate || toDate;

  // Calculate the number of active filters for badge
  const activeFilterCount = [fromDate ? 1 : 0, toDate ? 1 : 0].reduce(
    (sum, value) => sum + value,
    0,
  );

  // Handle removing filters directly from chips
  const handleRemoveFromDate = () => {
    setFromDate(null);
    setPage(1);
    refetch();
  };

  const handleRemoveToDate = () => {
    setToDate(null);
    setPage(1);
    refetch();
  };

  // Ensure subscriptions is an array
  const subscriptionsData = data?.items || [];

  console.log("Subscriptions data:", data);

  // Modal handlers for subscription details
  const openDetailModal = (transaction: SubscriptionTransaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTransaction(null);
  };

  // Modal handlers for subscription renewal
  const openRenewalModal = (transaction: SubscriptionTransaction) => {
    setSelectedTransaction(transaction);
    // Initialize renewal days object with zeros
    const daysObj: { [key: string]: number } = {};
    transaction.subscriptions.forEach((sub) => {
      daysObj[sub.id.toString()] = 30; // Default to 30 days, ensure ID is string
    });
    setRenewalDays(daysObj);
    setIsRenewalModalOpen(true);
  };

  const closeRenewalModal = () => {
    setIsRenewalModalOpen(false);
    setSelectedTransaction(null);
    setRenewalDays({});
  };

  // Handle renewal days change
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRenewalDaysChange = (subId: string, days: number) => {
    setRenewalDays((prev) => ({
      ...prev,
      [subId]: days,
    }));
  };

  // Calculate new expiry date
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateNewExpiryDate = (currentDate: string, daysToAdd: number) => {
    try {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + daysToAdd);
      return format(newDate, "MMM dd, yyyy HH:mm");
    } catch (error) {
      return "Invalid date";
    }
  };

  // Handle save renewals
  const handleSaveRenewals = async () => {
    if (!selectedTransaction) return;

    try {
      // Extract subscription IDs from the selected transaction
      const subscriptionIds = selectedTransaction.subscriptions.map(
        (sub) => sub.id,
      );

      // Prepare request payload
      const payload = {
        subscriptionIds,
        adjustDays: bulkRenewalDays,
      };

      console.log("Sending renewal request:", payload);

      // Send POST request to renew subscriptions
      await axios.post(`${ServerRoutes.subscriptions}/renewal`, payload);

      // Show success message or handle response as needed
      console.log("Subscriptions renewed successfully");

      // Close modal and refetch data
      closeRenewalModal();
      refetch();
    } catch (error) {
      // Handle errors
      console.error("Error renewing subscriptions:", error);
      // You could add error state handling here
    }
  };

  // Apply bulk renewal to all subscriptions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applyBulkRenewal = () => {
    if (!selectedTransaction) return;

    const updatedDays: { [key: string]: number } = {};
    selectedTransaction.subscriptions.forEach((sub) => {
      updatedDays[sub.id.toString()] = bulkRenewalDays;
    });

    setRenewalDays(updatedDays);
  };

  // Telegram message handlers
  const handleOpenTelegramModal = (userId: number) => {
    setSelectedUserId(userId);
    setTelegramMessage("");
    setIsTelegramModalOpen(true);
  };

  const handleCloseTelegramModal = () => {
    setIsTelegramModalOpen(false);
    setSelectedUserId(null);
    setTelegramMessage("");
  };

  const handleSendTelegramMessage = async () => {
    if (!selectedUserId || !telegramMessage.trim()) return;

    setIsSendingMessage(true);
    try {
      await axios.post("/admin/send-telegram-message", {
        userId: selectedUserId,
        message: telegramMessage.trim(),
      });

      setTelegramSnackbarMessage("Telegram message sent successfully!");
      setTelegramSnackbarSeverity("success");
      setTelegramSnackbarOpen(true);
      handleCloseTelegramModal();
    } catch (error) {
      console.error("Failed to send telegram message:", error);
      setTelegramSnackbarMessage(
        "Failed to send telegram message. Please try again.",
      );
      setTelegramSnackbarSeverity("error");
      setTelegramSnackbarOpen(true);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleCloseTelegramSnackbar = () => {
    setTelegramSnackbarOpen(false);
  };

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 4 },
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        background: "white",
        mt: 3,
      }}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 2 : 0}
        >
          <Typography variant="h5" fontWeight="bold" color="text.secondary">
            Subscriptions
          </Typography>
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            invisible={!hasActiveFilters}
            sx={{
              "& .MuiBadge-badge": {
                fontWeight: "bold",
              },
            }}
          >
            <Button
              startIcon={<FilterListIcon />}
              variant={hasActiveFilters ? "contained" : "outlined"}
              color="primary"
              onClick={openFilterModal}
              sx={{
                height: "40px",
                borderRadius: 1.5,
              }}
            >
              Filters
            </Button>
          </Badge>
        </Box>

        {hasActiveFilters && (
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {fromDate && (
              <Chip
                label={`From: ${fromDate.format("MMM DD, YYYY")}`}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={handleRemoveFromDate}
              />
            )}
            {toDate && (
              <Chip
                label={`To: ${toDate.format("MMM DD, YYYY")}`}
                color="primary"
                variant="outlined"
                size="small"
                onDelete={handleRemoveToDate}
              />
            )}
          </Stack>
        )}

        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            overflow: "hidden",
            width: "100%",
          }}
        >
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "text.secondary",
                    },
                  }}
                >
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Telegram ID</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  // Loading state
                  Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from(new Array(7)).map((_, cellIndex) => (
                        <TableCell key={cellIndex}>
                          <Skeleton animation="wave" height={24} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  // Error state
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="error">
                        Error loading subscriptions. Please try again.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : subscriptionsData.length === 0 ? (
                  // Empty state
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">
                        No subscriptions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  // Data state
                  subscriptionsData.map(
                    (transaction: SubscriptionTransaction) => {
                      // Get the first subscription if available
                      const firstSubscription =
                        transaction.subscriptions.length > 0
                          ? transaction.subscriptions[0]
                          : null;

                      return (
                        <TableRow
                          key={transaction.transactionId}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: "medium" }}>
                            {transaction.transactionId}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={transaction.product.description}>
                              <Typography fontWeight="medium">
                                {transaction.product.name}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{transaction.user?.id || "N/A"}</TableCell>
                          <TableCell>
                            {transaction.user?.telegramId || "N/A"}
                          </TableCell>
                          <TableCell>
                            {firstSubscription
                              ? formatDate(firstSubscription.expiredAt)
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            {firstSubscription && (
                              <Chip
                                label={
                                  isExpired(firstSubscription.expiredAt)
                                    ? "Expired"
                                    : "Active"
                                }
                                color={
                                  isExpired(firstSubscription.expiredAt)
                                    ? "error"
                                    : "success"
                                }
                                size="small"
                                sx={{
                                  fontWeight: "medium",
                                  minWidth: "80px",
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={actionButtonContainerStyle}>
                              <Tooltip title="View Subscriptions">
                                <IconButton
                                  size="small"
                                  sx={actionButtonStyles.primary}
                                  onClick={() => openDetailModal(transaction)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Renew Subscription">
                                <IconButton
                                  size="small"
                                  sx={actionButtonStyles.secondary}
                                  onClick={() => openRenewalModal(transaction)}
                                >
                                  <AutorenewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {transaction.user?.id && (
                                <Tooltip title="Send Telegram Message">
                                  <IconButton
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        "rgba(42, 171, 238, 0.05)",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(42, 171, 238, 0.15)",
                                      },
                                    }}
                                    onClick={() =>
                                      handleOpenTelegramModal(
                                        transaction.user!.id,
                                      )
                                    }
                                  >
                                    <TelegramIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {data?.meta?.totalPages && data.meta.totalPages > 1 && (
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={data.meta.totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              color="primary"
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        )}

        {/* Subscription Detail Modal */}
        <Dialog
          open={isDetailModalOpen}
          onClose={closeDetailModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Subscription Details
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransaction && (
              <Box display="flex" flexDirection="column" gap={3}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ReceiptIcon color="primary" fontSize="small" />
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="primary.main"
                    >
                      Transaction Information
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box
                      sx={{ flexBasis: { xs: "100%", sm: "45%", md: "30%" } }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Transaction ID
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        #{selectedTransaction.transactionId}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ flexBasis: { xs: "100%", sm: "45%", md: "30%" } }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Product
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTransaction.product.name}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ flexBasis: { xs: "100%", sm: "45%", md: "30%" } }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Price
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="success.main"
                      >
                        {selectedTransaction.product.price}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ flexBasis: { xs: "100%", sm: "45%", md: "30%" } }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Owner ID
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedTransaction.user?.id || "N/A"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{ flexBasis: { xs: "100%", sm: "45%", md: "30%" } }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Owner Telegram ID
                      </Typography>
                      <Tooltip
                        title={selectedTransaction.user?.telegramId || "N/A"}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedTransaction.user?.telegramId || "N/A"}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Subscriptions ({selectedTransaction.subscriptions.length})
                  </Typography>

                  {selectedTransaction.subscriptions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No subscriptions found
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Telegram ID</TableCell>
                            <TableCell>Chat ID</TableCell>
                            <TableCell>Expiry Date</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedTransaction.subscriptions.map((sub) => (
                            <TableRow key={sub.id}>
                              <TableCell>{sub.id}</TableCell>
                              <TableCell>{sub.telegramId}</TableCell>
                              <TableCell>{sub.chatId}</TableCell>
                              <TableCell>{formatDate(sub.expiredAt)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    isExpired(sub.expiredAt)
                                      ? "Expired"
                                      : "Active"
                                  }
                                  color={
                                    isExpired(sub.expiredAt)
                                      ? "error"
                                      : "success"
                                  }
                                  size="small"
                                  sx={{ fontWeight: "medium" }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={closeDetailModal}
              variant="contained"
              color="primary"
              sx={{ borderRadius: 1.5 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Subscription Renewal Modal */}
        <Dialog
          open={isRenewalModalOpen}
          onClose={closeRenewalModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            {selectedTransaction
              ? `Renew Subscription - ${selectedTransaction.product.name}`
              : "Renew Subscription"}
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransaction && (
              <Box display="flex" flexDirection="column" gap={3} py={2}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(0, 0, 0, 0.03)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Renewal Adjustment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Enter the number of days to add to all subscriptions in this
                    transaction.
                  </Typography>

                  <TextField
                    fullWidth
                    type="number"
                    label="Days to add"
                    value={bulkRenewalDays}
                    onChange={(e) =>
                      setBulkRenewalDays(parseInt(e.target.value) || 0)
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">days</InputAdornment>
                      ),
                    }}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={closeRenewalModal}
              variant="outlined"
              color="inherit"
              startIcon={<CloseIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: "medium",
                px: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRenewals}
              variant="contained"
              color="primary"
              startIcon={<AutorenewIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: "medium",
                px: 3,
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* Filter Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={closeFilterModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Filter Subscriptions
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              Expiry Date Range
            </Typography>
            <Box
              display="flex"
              gap={2}
              flexDirection={isMobile ? "column" : "row"}
            >
              <DatePicker
                label="From Date"
                value={tempFromDate}
                onChange={(date) => setTempFromDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
              <DatePicker
                label="To Date"
                value={tempToDate}
                onChange={(date) => setTempToDate(date)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={clearFilters} color="inherit">
            Clear All
          </Button>
          <Button onClick={closeFilterModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={applyFilters}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 1.5 }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Telegram Message Modal */}
      <Dialog
        open={isTelegramModalOpen}
        onClose={handleCloseTelegramModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TelegramIcon />
          Send Telegram Message
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={3} py={1}>
            <Typography variant="body2" color="text.secondary">
              Send a message to user ID: <strong>{selectedUserId}</strong>
            </Typography>
            <TextField
              label="Message"
              multiline
              rows={4}
              fullWidth
              value={telegramMessage}
              onChange={(e) => setTelegramMessage(e.target.value)}
              placeholder="Enter your message here..."
              variant="outlined"
              InputProps={{
                sx: {
                  borderRadius: 1.5,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseTelegramModal}
            color="inherit"
            disabled={isSendingMessage}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendTelegramMessage}
            variant="contained"
            color="primary"
            disabled={!telegramMessage.trim() || isSendingMessage}
            sx={{
              borderRadius: 1.5,
              minWidth: "100px",
            }}
          >
            {isSendingMessage ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Telegram Message Snackbar */}
      <Snackbar
        open={telegramSnackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseTelegramSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseTelegramSnackbar}
          severity={telegramSnackbarSeverity}
          sx={{ width: "100%" }}
        >
          {telegramSnackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}
